import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useSearchParams, Navigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { visibleAdminTabs, can } from "@/constants/adminTabs";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import RefreshButton from "@/components/RefreshButton";
import UsersTab from "@/pages/admin/UsersTab";
import WarehousesTab from "@/pages/admin/WarehousesTab";
import TenantsTab from "@/pages/admin/TenantsTab";
import MyRouteTab from "@/pages/admin/MyRouteTab";
import TestDataTab from "@/pages/admin/TestDataTab";
import DbBackupTab from "@/pages/admin/DbBackupTab";
import SettingsTab from "@/pages/admin/SettingsTab";
import ImportResultDialog from "@/pages/admin/ImportResultDialog";
import InboundPanel, { InboundTabs } from "@/components/inbound/InboundPanel";
import { errorText } from "@/pages/admin/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, Upload, Users, Package, Store, Tag, Search, ChevronLeft, ChevronRight } from "lucide-react";
import Toast from "@/components/Toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };
// Backend list caps (catalog_routes.py); a full list means rows may be missing.
const PRODUCT_LIST_CAP = 500;
const FNSKU_LIST_CAP = 1000;

function TablePagination({ page, total, pageSize, setPage, setPageSize }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-white rounded-b-2xl">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <span>Rows:</span>
        {[25, 50, 100].map(n => (
          <button key={n} onClick={() => { setPageSize(n); setPage(1); }}
            className={`px-2 py-0.5 rounded font-medium transition-colors ${pageSize === n ? "bg-blue-100 text-blue-700" : "hover:bg-slate-100 text-slate-600"}`}>
            {n}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <span>{from}–{to} / {total}</span>
        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="font-semibold text-slate-700 w-8 text-center">{page}/{pages}</span>
        <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function ColFilter({ value, onChange }) {
  const { t } = useLang();
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={t("common.filterPlaceholder")}
      className="mt-1.5 w-full h-7 px-2 text-xs normal-case font-normal bg-white border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 placeholder:text-slate-300"
      onClick={e => e.stopPropagation()}
    />
  );
}

// Must match the `detail` of the 400 raised by POST /products in backend/server.py.
const DUPLICATE_ASIN_DETAIL = "ASIN already exists";

const TH = "px-4 pt-3 pb-2 text-left align-top";
const TH_LABEL = "text-[10px] font-bold uppercase tracking-widest text-slate-500 block";

export default function Admin() {
  const { t } = useLang();
  const { user } = useAuth();
  const [msg, setMsg] = useState(null);
  const showMsg = useCallback((type, text) => setMsg({ type, text }), []);

  // Sidebar deep-links navigate here with ?tab=; only tabs the caller may use are valid.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = visibleAdminTabs(user);
  const tabIds = tabs.map(x => x.tab);
  const requestedTab = searchParams.get("tab");
  const activeTab = tabIds.includes(requestedTab) ? requestedTab : null;
  const setActiveTab = (tab) => setSearchParams({ tab });
  const [inboundView, setInboundView] = useState("inbound");

  // Users
  const [users, setUsers] = useState([]);

  // Products
  const [products, setProducts] = useState([]);
  const [prodSearch, setProdSearch] = useState("");
  const [showProdModal, setShowProdModal] = useState(false);
  const [prodForm, setProdForm] = useState({ asin: "", product_name: "", ean: "", category: "" });
  const [importLoading, setImportLoading] = useState(false);
  const [prodColFilters, setProdColFilters] = useState({ name: "", asin: "", ean: "", category: "" });
  const [prodPage, setProdPage] = useState(1);
  const [prodPageSize, setProdPageSize] = useState(25);

  // Stores
  const [stores, setStores] = useState([]);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [storePage, setStorePage] = useState(1);
  const [storePageSize, setStorePageSize] = useState(25);

  // FNSKU
  const [fnskus, setFnskus] = useState([]);
  const [showFNSKUModal, setShowFNSKUModal] = useState(false);
  const [fnskuForm, setFnskuForm] = useState({ asin: "", store_id: "", fnsku: "" });
  const [fnskuColFilters, setFnskuColFilters] = useState({ asin: "", store: "", fnsku: "" });
  const [fnskuPage, setFnskuPage] = useState(1);
  const [fnskuPageSize, setFnskuPageSize] = useState(25);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [stats, setStats] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // ── Computed filtered + paginated data ────────────────
  const filteredProds = useMemo(() => products.filter(p =>
    p.product_name?.toLowerCase().includes(prodColFilters.name.toLowerCase()) &&
    p.asin?.toLowerCase().includes(prodColFilters.asin.toLowerCase()) &&
    (p.ean || "").toLowerCase().includes(prodColFilters.ean.toLowerCase()) &&
    (p.category || "").toLowerCase().includes(prodColFilters.category.toLowerCase())
  ), [products, prodColFilters]);

  const pagedProds = useMemo(() =>
    filteredProds.slice((prodPage - 1) * prodPageSize, prodPage * prodPageSize),
    [filteredProds, prodPage, prodPageSize]);

  const filteredStores = useMemo(() =>
    stores.filter(s => s.store_name?.toLowerCase().includes(storeFilter.toLowerCase())),
    [stores, storeFilter]);

  const pagedStores = useMemo(() =>
    filteredStores.slice((storePage - 1) * storePageSize, storePage * storePageSize),
    [filteredStores, storePage, storePageSize]);

  const filteredFnskus = useMemo(() => fnskus.filter(f =>
    f.asin?.toLowerCase().includes(fnskuColFilters.asin.toLowerCase()) &&
    f.store_name?.toLowerCase().includes(fnskuColFilters.store.toLowerCase()) &&
    f.fnsku?.toLowerCase().includes(fnskuColFilters.fnsku.toLowerCase())
  ), [fnskus, fnskuColFilters]);

  const pagedFnskus = useMemo(() =>
    filteredFnskus.slice((fnskuPage - 1) * fnskuPageSize, fnskuPage * fnskuPageSize),
    [filteredFnskus, fnskuPage, fnskuPageSize]);

  // Reset page on filter change
  useEffect(() => setProdPage(1), [prodColFilters, prodSearch]);
  useEffect(() => setStorePage(1), [storeFilter]);
  useEffect(() => setFnskuPage(1), [fnskuColFilters]);

  // ── Data fetching ──────────────────────────────────────
  // Each list is loaded only when the caller has the permission; a failed list stays empty.
  const canSeeProducts = can(user, "products:create") || can(user, "products:manage");
  const fetchAll = async () => {
    const load = (allowed, url, set) => allowed
      ? axios.get(url, ax).then(r => set(r.data)).catch(() => {})
      : Promise.resolve();
    await Promise.all([
      load(can(user, "users:manage"), `${API}/users`, setUsers),
      load(canSeeProducts, `${API}/products?q=${encodeURIComponent(prodSearch)}`, setProducts),
      load(can(user, "stores:manage"), `${API}/stores`, setStores),
      load(can(user, "stores:manage"), `${API}/store-fnsku`, setFnskus),
      axios.get(`${API}/stats`, ax).then(r => setStats(r.data)).catch(() => {}),
    ]);
  };

  useEffect(() => { fetchAll(); }, []);

  // Refresh button and background refresh: this page's lists plus the open tab's own data (they watch `refreshTick`).
  const [refreshTick, setRefreshTick] = useState(0);
  const reloadScreen = async () => {
    await fetchAll();
    setRefreshTick(n => n + 1);
  };
  const { refresh, refreshing } = useAutoRefresh(reloadScreen, {
    paused: !!deleteTarget || showProdModal || showStoreModal || showFNSKUModal,
  });

  // Users change from other tabs too (tenant administrators), so reload when the tab opens.
  useEffect(() => {
    if (activeTab === "users" && can(user, "users:manage")) {
      axios.get(`${API}/users`, ax).then(r => setUsers(r.data)).catch(() => {});
    }
  }, [activeTab]);

  useEffect(() => {
    if (!canSeeProducts) return undefined;
    const timer = setTimeout(() => {
      axios.get(`${API}/products?q=${encodeURIComponent(prodSearch)}`, ax).then(r => setProducts(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [prodSearch]);

  // ── Users ──────────────────────────────────────────────
  const deleteUser = async (id) => {
    try {
      await axios.delete(`${API}/users/${id}`, ax);
      showMsg("success", t("adm.us.deleted")); fetchAll();
    } catch (e) { showMsg("error", e.response?.data?.detail || t("adm.error")); }
    setDeleteTarget(null);
  };

  // ── Products ───────────────────────────────────────────
  const saveProduct = async () => {
    try {
      await axios.post(`${API}/products`, prodForm, ax);
      showMsg("success", t("adm.pr.created"));
      setShowProdModal(false); setProdForm({ asin: "", product_name: "", ean: "", category: "" }); fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      showMsg("error", detail === DUPLICATE_ASIN_DETAIL ? t("adm.pr.duplicate") : (typeof detail === "string" ? detail : t("adm.error")));
    }
  };
  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API}/products/${id}`, ax);
      showMsg("success", t("adm.pr.deleted")); fetchAll();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    setDeleteTarget(null);
  };
  const importProducts = async (file) => {
    setImportLoading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/products/bulk-import`, form, { ...ax, headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data); fetchAll();
    } catch (e) { showMsg("error", e.response?.data?.detail || t("adm.import.failed")); }
    finally { setImportLoading(false); }
  };
  const importFNSKU = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/store-fnsku/bulk-import`, form, { ...ax, headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data); fetchAll();
    } catch (e) { showMsg("error", e.response?.data?.detail || t("adm.import.failed")); }
  };

  // ── Stores ─────────────────────────────────────────────
  const saveStore = async () => {
    if (!storeName.trim()) return;
    try {
      await axios.post(`${API}/stores`, { store_name: storeName }, ax);
      showMsg("success", t("adm.st.created"));
      setShowStoreModal(false); setStoreName(""); fetchAll();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };
  const deleteStore = async (id) => {
    try {
      await axios.delete(`${API}/stores/${id}`, ax);
      showMsg("success", t("adm.st.deleted")); fetchAll();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    setDeleteTarget(null);
  };

  // ── FNSKU ──────────────────────────────────────────────
  const saveFNSKU = async () => {
    try {
      await axios.post(`${API}/store-fnsku`, fnskuForm, ax);
      showMsg("success", t("adm.fn.saved"));
      setShowFNSKUModal(false); setFnskuForm({ asin: "", store_id: "", fnsku: "" }); fetchAll();
    } catch (e) { showMsg("error", e.response?.data?.detail || t("adm.error")); }
  };
  const deleteFNSKU = async (id) => {
    try {
      await axios.delete(`${API}/store-fnsku/${id}`, ax);
      showMsg("success", t("adm.deleted")); fetchAll();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    setDeleteTarget(null);
  };

  // ── Render ─────────────────────────────────────────────
  if (!activeTab) {
    return <Navigate to={tabs.length ? `/admin?tab=${tabs[0].tab}` : "/dashboard"} replace />;
  }

  return (
    <div className="space-y-4 pb-4">
      <Toast msg={msg} onClose={() => setMsg(null)} />
      <ImportResultDialog result={importResult} onClose={() => setImportResult(null)} />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
          {t("nav.admin")}
        </h1>
        <RefreshButton onClick={refresh} refreshing={refreshing} />
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: t("adm.tab.users"), val: users.length, icon: Users, color: "text-slate-700 bg-slate-50 border-slate-200" },
            { label: t("admin.products"), val: stats.products, icon: Package, color: "text-blue-700 bg-blue-50 border-blue-200" },
            { label: t("admin.stores"), val: stats.stores, icon: Store, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
            { label: t("admin.store_fnsku"), val: fnskus.length, icon: Tag, color: "text-amber-700 bg-amber-50 border-amber-200" },
          ].map(({ label, val, icon: Icon, color }) => (
            <div key={label} className={`rounded-xl border p-3 ${color}`}>
              <div className="flex items-center justify-between">
                <p className="text-2xl font-extrabold">{val}</p>
                <Icon className="w-4 h-4 opacity-60" />
              </div>
              <p className="text-xs font-medium mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full overflow-x-auto flex-nowrap">
          {tabs.map(({ tab, icon: Icon }) => (
            <TabsTrigger key={tab} value={tab} className="flex-1 flex items-center gap-1.5 text-xs sm:text-sm">
              <Icon className="w-3.5 h-3.5" />{t(`adm.tab.${tab}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="tenants" className="mt-4">
          <TenantsTab showMsg={showMsg} refreshTick={refreshTick} />
        </TabsContent>
        <TabsContent value="warehouses" className="mt-4">
          <WarehousesTab showMsg={showMsg} refreshTick={refreshTick} />
        </TabsContent>
        <TabsContent value="route" className="mt-4">
          <MyRouteTab showMsg={showMsg} refreshTick={refreshTick} />
        </TabsContent>
        <TabsContent value="testdata" className="mt-4">
          <TestDataTab showMsg={showMsg} refreshTick={refreshTick} />
        </TabsContent>
        <TabsContent value="dbbackup" className="mt-4">
          <DbBackupTab showMsg={showMsg} refreshTick={refreshTick} />
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          <SettingsTab showMsg={showMsg} />
        </TabsContent>
        <TabsContent value="inbound" className="mt-4 space-y-3">
          <InboundTabs value={inboundView} onChange={setInboundView} withPallets={false} />
          <InboundPanel side="platform" view={inboundView} />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <UsersTab users={users} reload={fetchAll} currentUser={user} requestDelete={setDeleteTarget} showMsg={showMsg} />
        </TabsContent>

        {/* ── Products Tab ─────────────────────────────── */}
        <TabsContent value="products" className="mt-4 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={prodSearch} onChange={e => setProdSearch(e.target.value)}
                placeholder={t("adm.pr.search")} className="pl-9 h-9" />
            </div>
            {can(user, "products:create") && (<>
              <Button size="sm" onClick={() => setShowProdModal(true)} className="gap-1.5 bg-blue-600 hover:bg-blue-700 h-9">
                <Plus className="w-4 h-4" /> {t("common.create")}
              </Button>
              <label className="cursor-pointer">
                <input type="file" className="sr-only" accept=".csv,.xlsx" onChange={e => { const f = e.target.files[0]; e.target.value = ""; if (f) importProducts(f); }} />
                <span className="flex items-center gap-1.5 px-3 h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md cursor-pointer transition-colors">
                  {importLoading ? "…" : <><Upload className="w-3.5 h-3.5" /> {t("adm.import.button")}</>}
                </span>
              </label>
            </>)}
          </div>

          {products.length >= PRODUCT_LIST_CAP && <p className="text-xs text-amber-700">{t("adm.capped_search", { count: PRODUCT_LIST_CAP })}</p>}
          <div className="rounded-2xl border border-slate-200 shadow-sm bg-white">
            <table className="w-full border-collapse min-w-[560px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 border-b border-slate-200">
                  <th className={`${TH} w-[38%]`}>
                    <span className={TH_LABEL}>{t("adm.pr.name")}</span>
                    <ColFilter value={prodColFilters.name} onChange={v => setProdColFilters(f => ({ ...f, name: v }))} />
                  </th>
                  <th className={`${TH} w-[22%]`}>
                    <span className={TH_LABEL}>ASIN</span>
                    <ColFilter value={prodColFilters.asin} onChange={v => setProdColFilters(f => ({ ...f, asin: v }))} />
                  </th>
                  <th className={`${TH} w-[18%] hidden sm:table-cell`}>
                    <span className={TH_LABEL}>EAN</span>
                    <ColFilter value={prodColFilters.ean} onChange={v => setProdColFilters(f => ({ ...f, ean: v }))} />
                  </th>
                  <th className={`${TH} hidden sm:table-cell`}>
                    <span className={TH_LABEL}>{t("admin.category")}</span>
                    <ColFilter value={prodColFilters.category} onChange={v => setProdColFilters(f => ({ ...f, category: v }))} />
                  </th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {pagedProds.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400 text-sm">{t("adm.pr.empty")}</td></tr>
                ) : pagedProds.map(p => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-2.5 text-sm font-medium text-slate-900 max-w-0">
                      <span className="block truncate">{p.product_name}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs font-mono text-slate-500">{p.asin}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 hidden sm:table-cell">{p.ean}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 hidden sm:table-cell">{p.category}</td>
                    <td className="px-2 py-2.5">
                      {can(user, "products:manage") && (
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteTarget({ type: "product", id: p.id, name: p.product_name })}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination page={prodPage} total={filteredProds.length} pageSize={prodPageSize} setPage={setProdPage} setPageSize={setProdPageSize} />
          </div>
          {can(user, "products:create") && <p className="text-xs text-slate-500">{t("adm.pr.csv_hint")}</p>}
        </TabsContent>

        {/* ── Stores Tab ───────────────────────────────── */}
        <TabsContent value="stores" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setShowStoreModal(true)} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4" /> {t("adm.st.new")}
            </Button>
          </div>

          <div className="rounded-2xl border border-slate-200 shadow-sm bg-white">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 border-b border-slate-200">
                  <th className={`${TH} w-10`} />
                  <th className={TH}>
                    <span className={TH_LABEL}>{t("adm.st.name")}</span>
                    <ColFilter value={storeFilter} onChange={setStoreFilter} />
                  </th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {pagedStores.length === 0 ? (
                  <tr><td colSpan={3} className="p-8 text-center text-slate-400 text-sm">{t("adm.st.empty")}</td></tr>
                ) : pagedStores.map(s => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <Store className="w-4 h-4 text-slate-400" />
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-900">{s.store_name}</td>
                    <td className="px-2 py-3">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => setDeleteTarget({ type: "store", id: s.id, name: s.store_name })}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination page={storePage} total={filteredStores.length} pageSize={storePageSize} setPage={setStorePage} setPageSize={setStorePageSize} />
          </div>
        </TabsContent>

        {/* ── FNSKU Tab ────────────────────────────────── */}
        <TabsContent value="fnsku" className="mt-4 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => setShowFNSKUModal(true)} className="gap-1.5 bg-blue-600 hover:bg-blue-700 h-9">
              <Plus className="w-4 h-4" /> {t("adm.fn.add")}
            </Button>
            <label className="cursor-pointer">
              <input type="file" className="sr-only" accept=".csv,.xlsx" onChange={e => { const f = e.target.files[0]; e.target.value = ""; if (f) importFNSKU(f); }} />
              <span className="flex items-center gap-1.5 px-3 h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md cursor-pointer transition-colors">
                <Upload className="w-3.5 h-3.5" /> {t("adm.import.button")}
              </span>
            </label>
          </div>

          {fnskus.length >= FNSKU_LIST_CAP && <p className="text-xs text-amber-700">{t("adm.capped", { count: FNSKU_LIST_CAP })}</p>}
          <div className="rounded-2xl border border-slate-200 shadow-sm bg-white">
            <table className="w-full border-collapse min-w-[480px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 border-b border-slate-200">
                  <th className={`${TH} w-[30%]`}>
                    <span className={TH_LABEL}>ASIN</span>
                    <ColFilter value={fnskuColFilters.asin} onChange={v => setFnskuColFilters(f => ({ ...f, asin: v }))} />
                  </th>
                  <th className={`${TH} w-[35%]`}>
                    <span className={TH_LABEL}>{t("common.store")}</span>
                    <ColFilter value={fnskuColFilters.store} onChange={v => setFnskuColFilters(f => ({ ...f, store: v }))} />
                  </th>
                  <th className={TH}>
                    <span className={TH_LABEL}>FNSKU</span>
                    <ColFilter value={fnskuColFilters.fnsku} onChange={v => setFnskuColFilters(f => ({ ...f, fnsku: v }))} />
                  </th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {pagedFnskus.length === 0 ? (
                  <tr><td colSpan={4} className="p-8 text-center text-slate-400 text-sm">{t("adm.fn.empty")}</td></tr>
                ) : pagedFnskus.map(f => (
                  <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-2.5 text-xs font-mono text-slate-500">{f.asin}</td>
                    <td className="px-4 py-2.5 text-sm font-semibold text-slate-900">{f.store_name}</td>
                    <td className="px-4 py-2.5 text-xs font-mono text-blue-600">{f.fnsku}</td>
                    <td className="px-2 py-2.5">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => setDeleteTarget({ type: "fnsku", id: f.id, name: f.fnsku })}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination page={fnskuPage} total={filteredFnskus.length} pageSize={fnskuPageSize} setPage={setFnskuPage} setPageSize={setFnskuPageSize} />
          </div>
          <p className="text-xs text-slate-500">{t("adm.fn.csv_hint")}</p>
        </TabsContent>
      </Tabs>


      {/* ── Product Modal ───────────────────────────────── */}
      <Dialog open={showProdModal} onOpenChange={setShowProdModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("adm.pr.new")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[{ field: "asin", label: "ASIN *" }, { field: "product_name", label: `${t("adm.pr.name")} *` }, { field: "ean", label: `EAN (${t("adm.optional")})` }, { field: "category", label: `${t("admin.category")} (${t("adm.optional")})` }].map(({ field, label }) => (
              <div key={field} className="space-y-1.5">
                <Label>{label}</Label>
                <Input value={prodForm[field]} onChange={e => setProdForm(f => ({ ...f, [field]: e.target.value }))} className="h-11" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProdModal(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveProduct} className="bg-blue-600 hover:bg-blue-700">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Store Modal ─────────────────────────────────── */}
      <Dialog open={showStoreModal} onOpenChange={setShowStoreModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("adm.st.new")}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("adm.st.name")} *</Label>
            <Input value={storeName} onChange={e => setStoreName(e.target.value)} className="h-11"
              onKeyDown={e => e.key === "Enter" && saveStore()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStoreModal(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveStore} className="bg-blue-600 hover:bg-blue-700">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── FNSKU Modal ─────────────────────────────────── */}
      <Dialog open={showFNSKUModal} onOpenChange={setShowFNSKUModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("adm.fn.add")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>ASIN *</Label>
              <Input value={fnskuForm.asin} onChange={e => setFnskuForm(f => ({ ...f, asin: e.target.value }))} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.store")} *</Label>
              <Select value={fnskuForm.store_id} onValueChange={v => setFnskuForm(f => ({ ...f, store_id: v }))}>
                <SelectTrigger className="h-11"><SelectValue placeholder={t("adm.fn.select_store")} /></SelectTrigger>
                <SelectContent>
                  {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("adm.fn.code")} *</Label>
              <Input value={fnskuForm.fnsku} onChange={e => setFnskuForm(f => ({ ...f, fnsku: e.target.value }))} className="h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFNSKUModal(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveFNSKU} disabled={!fnskuForm.asin || !fnskuForm.store_id || !fnskuForm.fnsku} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Confirm Delete ──────────────────────────────── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("adm.delete_title", { name: deleteTarget?.name ?? "" })}</AlertDialogTitle>
            <p className="text-sm text-muted-foreground">{t("adm.delete_warning")}</p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deleteTarget?.type === "user") deleteUser(deleteTarget.id);
                else if (deleteTarget?.type === "product") deleteProduct(deleteTarget.id);
                else if (deleteTarget?.type === "store") deleteStore(deleteTarget.id);
                else if (deleteTarget?.type === "fnsku") deleteFNSKU(deleteTarget.id);
              }}>{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
